import type * as React from 'react'

import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

interface CustomerSidebarActionProps extends React.ComponentProps<typeof Button> {
  icon: React.ReactNode
  label: string
  /** Hover hint, for a row whose label doesn't say why it's disabled or where it goes. */
  hint?: React.ReactNode
  /** Wraps the button in an opener (a dialog trigger), keeping the tooltip outside it. */
  wrap?: (trigger: React.ReactNode) => React.ReactNode
}

/** One row of the customer sidebar: a full-width labelled button. */
export function CustomerSidebarAction({
  icon,
  label,
  hint,
  wrap,
  className,
  disabled,
  size = 'sm',
  ...props
}: CustomerSidebarActionProps) {
  const button = (
    <Button size={size} disabled={disabled} className={cn('w-full justify-start', className)} {...props}>
      {icon}
      {label}
    </Button>
  )

  if (!hint) return wrap ? wrap(button) : button

  const trigger = (
    <TooltipTrigger asChild>
      {/* A disabled button fires no pointer events, so the tooltip hangs off a focusable wrapper. */}
      {disabled ? (
        <span tabIndex={0} className="block">
          {button}
        </span>
      ) : (
        button
      )}
    </TooltipTrigger>
  )

  return (
    <Tooltip>
      {wrap ? wrap(trigger) : trigger}
      <TooltipContent>{hint}</TooltipContent>
    </Tooltip>
  )
}
